<template>
    <div class="max-w-6xl mx-auto pb-8 space-y-6">
        <Loading :overlay="true" :show="saving" overlay-text="salvando edição..." />
        <Loading :overlay="true" :show="exportingPdf" overlay-text="montando PDF completo..." />

        <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
                <div class="flex flex-wrap items-center gap-2">
                    <h1 class="text-2xl font-bold text-gray-800">ACIA</h1>
                    <span class="rounded-full bg-gradient-to-r from-cyan-600 to-cyan-400 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white shadow-sm">
                        Athlete Clinical Intelligence & Analytics
                    </span>
                </div>
                <p class="mt-1 text-sm text-gray-500">Relatório premium que cruza avaliação, exames, matriz de risco e perfil do atleta.</p>
            </div>

            <div class="flex flex-wrap gap-2">
                <BaseButton skin="primary-outline-small" @click="goToRecords">Voltar ao prontuário</BaseButton>
                <BaseButton v-if="acias?.record_uuid" skin="primary-outline-small" @click="goToAvaliation">Abrir avaliação</BaseButton>
                <BaseButton v-if="acias?.status === 'done'" skin="primary-small" :disabled="exportingPdf" @click="exportPdf">
                    {{ exportingPdf ? 'Exportando...' : 'Exportar PDF' }}
                </BaseButton>
            </div>
        </div>

        <template v-if="loading">
            <section class="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
                <div class="skeleton h-7 w-64 rounded-xl"></div>
                <div class="mt-2 skeleton h-4 w-96 rounded"></div>
                <div class="mt-6 flex flex-wrap gap-2">
                    <div v-for="i in 4" :key="`chip-${i}`" class="skeleton h-8 w-36 rounded-full"></div>
                </div>
                <div class="mt-6 grid gap-4 sm:grid-cols-3">
                    <div v-for="i in 3" :key="`meta-${i}`" class="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                        <div class="skeleton h-3 w-24 rounded"></div>
                        <div class="mt-3 skeleton h-4 w-32 rounded"></div>
                    </div>
                </div>
            </section>

            <section class="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
                <div class="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_340px]">
                    <div class="skeleton min-h-[480px] rounded-[28px]"></div>
                    <div class="space-y-4">
                        <div class="skeleton h-44 rounded-[24px]"></div>
                        <div class="skeleton h-56 rounded-[24px]"></div>
                        <div class="skeleton h-48 rounded-[24px]"></div>
                    </div>
                </div>
            </section>
        </template>

        <template v-else-if="acias">
            <!-- Cabeçalho da análise -->
            <section class="overflow-hidden rounded-[28px] border border-gray-100 bg-white shadow-sm">
                <div class="bg-[radial-gradient(circle_at_top_left,_rgba(236,254,255,0.95),_rgba(248,250,252,0.98)_45%,_rgba(255,255,255,1))] px-6 py-6">
                    <div class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                        <div class="min-w-0">
                            <h2 class="text-xl font-bold text-slate-800">{{ acias.patient_name || 'Paciente' }}</h2>
                            <p class="mt-1 text-sm text-slate-500">
                                <template v-if="acias.record_uuid">Baseada na avaliação de {{ formatDate(acias.record_created_at) }}</template>
                                <template v-else>Somente documentos, sem avaliação funcional</template>
                                · gerada {{ formatRelative(acias.generated_at) }}
                            </p>

                            <div class="mt-4">
                                <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Documentos cruzados</div>
                                <div class="mt-2 flex flex-wrap gap-2">
                                    <span
                                        v-for="file in acias.files_used || []"
                                        :key="file.id"
                                        class="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm ring-1 ring-slate-200"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor" class="h-3.5 w-3.5 text-cyan-600">
                                            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                        </svg>
                                        {{ file.filename }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="grid shrink-0 gap-3 sm:grid-cols-2 xl:grid-cols-1">
                            <div class="rounded-2xl border border-slate-100 bg-white px-4 py-4 shadow-sm">
                                <div class="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Gerada em</div>
                                <div class="mt-1 text-sm font-semibold text-slate-700">{{ formatDateTime(acias.generated_at) }}</div>
                            </div>
                            <div class="rounded-2xl border border-slate-100 bg-white px-4 py-4 shadow-sm">
                                <div class="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Cruzamentos</div>
                                <div class="mt-1 text-sm font-semibold text-slate-700">{{ (acias.key_correlations || []).length }} correlações identificadas</div>
                            </div>
                        </div>
                    </div>

                    <div v-if="acias.executive_summary" class="mt-5 rounded-2xl border border-cyan-100 bg-cyan-50/80 px-5 py-4">
                        <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-700">Resumo executivo</div>
                        <p class="mt-2 text-sm leading-6 text-cyan-950">{{ acias.executive_summary }}</p>
                    </div>
                </div>
            </section>

            <!-- Em andamento: a geração roda no servidor, esta tela só acompanha -->
            <AciasGeneratingCard
                v-if="acias.status === 'generating'"
                :files-count="(acias.files_used || []).length"
                :started-at="acias.created_at"
            />

            <section v-else-if="acias.status === 'failed'" class="rounded-[28px] border border-rose-200 bg-rose-50 p-6 shadow-sm">
                <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div class="text-sm text-rose-900">
                        {{ failureMessage }} Você pode tentar novamente com os mesmos documentos.
                    </div>
                    <BaseButton skin="primary-small" :disabled="regenerating" @click="regenerate">
                        {{ regenerating ? 'Enviando...' : 'Tentar novamente' }}
                    </BaseButton>
                </div>
            </section>

            <!-- Relatório -->
            <section v-else class="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
                <div class="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_340px]">
                    <div>
                        <div class="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Relatório editável</div>
                        <div
                            contenteditable="true"
                            class="prose prose-sm min-h-[520px] max-w-none rounded-[28px] border border-slate-200 bg-white px-5 py-5 text-sm leading-7 text-slate-700 shadow-sm focus:border-cyan-300 focus:outline-none prose-headings:font-semibold prose-headings:text-cyan-700 prose-p:my-4 prose-p:text-slate-600 prose-li:text-slate-600 prose-table:text-xs"
                            v-html="draft"
                            @input="draft = $event.target.innerHTML"
                        ></div>
                    </div>

                    <div class="space-y-4">
                        <div class="rounded-[24px] border border-slate-100 bg-slate-50/80 p-5">
                            <div class="text-sm font-semibold text-slate-800">Ações</div>
                            <div class="mt-4 flex flex-col gap-2">
                                <button type="button" class="btn btn-primary" :disabled="saving || regenerating" @click="save">
                                    {{ saving ? 'Salvando...' : 'Salvar edição' }}
                                </button>
                                <button type="button" class="btn btn-outline" :disabled="exportingPdf || regenerating" @click="exportPdf">
                                    {{ exportingPdf ? 'Exportando...' : 'Exportar PDF completo' }}
                                </button>
                                <button type="button" class="btn acia-ai-cta flex gap-x-2" :disabled="saving || regenerating" @click="regenerate">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                                    </svg>
                                    {{ regenerating ? 'Enviando...' : 'Gerar novamente com IA' }}
                                </button>
                            </div>
                            <p class="mt-3 text-[11px] leading-4 text-slate-400">
                                O PDF sai com o relatório editado, o quadro de cruzamentos, o inventário de documentos e os gráficos.
                            </p>
                        </div>

                        <div class="rounded-[24px] border border-slate-100 bg-slate-50/80 p-5">
                            <div class="text-sm font-semibold text-slate-800">Documentos analisados</div>
                            <div v-if="(acias.documents_analyzed || []).length" class="mt-4 space-y-2">
                                <div
                                    v-for="(doc, index) in acias.documents_analyzed"
                                    :key="`doc-${index}`"
                                    class="rounded-[20px] bg-white px-4 py-3 shadow-sm ring-1 ring-slate-100"
                                >
                                    <div class="flex items-center justify-between gap-2">
                                        <div class="min-w-0 truncate text-xs font-semibold text-slate-700">{{ doc.filename }}</div>
                                        <span class="shrink-0 rounded-full bg-cyan-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-cyan-700">
                                            {{ documentTypeLabel(doc.document_type) }}
                                        </span>
                                    </div>
                                    <ul v-if="(doc.key_findings || []).length" class="mt-2 space-y-1 text-xs leading-5 text-slate-500">
                                        <li v-for="(finding, fi) in doc.key_findings" :key="`finding-${index}-${fi}`" class="flex gap-1.5">
                                            <span class="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-cyan-400"></span>
                                            <span>{{ finding }}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div v-else class="mt-4 text-sm text-slate-400">Nenhum inventário estruturado registrado.</div>
                        </div>

                        <div class="rounded-[24px] border border-slate-100 bg-slate-50/80 p-5">
                            <div class="text-sm font-semibold text-slate-800">Cruzamentos identificados</div>
                            <div v-if="(acias.key_correlations || []).length" class="mt-4 space-y-2">
                                <div
                                    v-for="(correlation, index) in acias.key_correlations"
                                    :key="`correlation-${index}`"
                                    class="rounded-[22px] bg-white px-4 py-4 text-sm text-slate-600 shadow-sm ring-1 ring-slate-100"
                                >
                                    <div class="flex items-center justify-between gap-2">
                                        <div class="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Cruzamento {{ index + 1 }}</div>
                                        <span
                                            class="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em]"
                                            :class="strengthClass(correlation.strength)"
                                        >
                                            {{ strengthLabel(correlation.strength) }}
                                        </span>
                                    </div>

                                    <div class="mt-3 space-y-3">
                                        <div>
                                            <div class="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-600">Achado A</div>
                                            <div class="mt-1 leading-6 text-slate-700">{{ correlation.finding_a }}</div>
                                        </div>
                                        <div>
                                            <div class="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-600">Achado B</div>
                                            <div class="mt-1 leading-6 text-slate-700">{{ correlation.finding_b }}</div>
                                        </div>
                                        <div>
                                            <div class="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-600">Leitura clínica</div>
                                            <div class="mt-1 leading-6 text-slate-700">{{ correlation.clinical_significance }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="mt-4 text-sm text-slate-400">Nenhuma correlação estruturada registrada.</div>
                        </div>
                    </div>
                </div>
            </section>
        </template>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Swal from 'sweetalert';
import Loading from '@/components/Loading';
import AciasGeneratingCard from '@/components/AciasGeneratingCard.vue';
import { AciasService, UsageService } from '@/services/Entity';
import { unwrapAiHtml } from '@shared/ai';
import { PLANS } from '@/constants/plans';

// A geração roda no servidor: esta tela acompanha o registro por polling
const POLL_INTERVAL_MS = 6000;

const FAILURE_MESSAGES = {
    timeout:                   'A geração passou do tempo limite e foi interrompida.',
    no_valid_files:            'Os arquivos vinculados não puderam ser lidos.',
    openrouter_not_configured: 'A integração com a IA não está configurada neste ambiente.',
    openrouter_failed:         'A IA não respondeu a tempo.'
};

export default {
    components: {
        Loading,
        AciasGeneratingCard
    },
    setup() {
        const route = useRoute();
        const router = useRouter();
        const loading = ref(true);
        const saving = ref(false);
        const exportingPdf = ref(false);
        const regenerating = ref(false);
        const acias = ref(null);
        const draft = ref('');
        const highestPlanId = PLANS[PLANS.length - 1]?.id || 'standard';
        let pollTimer = null;

        const failureMessage = computed(() => {
            return FAILURE_MESSAGES[acias.value?.error_code] || 'Esta geração falhou antes de concluir.';
        });

        onMounted(async () => {
            await loadPage();
        });

        onBeforeUnmount(() => {
            if (pollTimer) clearInterval(pollTimer);
        });

        // Liga o polling enquanto a ACIA está sendo gerada e desliga quando fecha
        function syncPolling() {
            const generating = acias.value?.status === 'generating';

            if (generating && !pollTimer) {
                pollTimer = setInterval(refreshStatus, POLL_INTERVAL_MS);
                return;
            }

            if (!generating && pollTimer) {
                clearInterval(pollTimer);
                pollTimer = null;
            }
        }

        async function refreshStatus() {
            const aciasUuid = route.params.acias_uuid;
            if (!aciasUuid) return;

            try {
                const { data } = await AciasService.getByUUID(aciasUuid);
                const fresh = (data && data.data) || null;
                if (!fresh) return;

                acias.value = fresh;
                // Só assume o texto quando a geração fecha — não pisa em edição do usuário
                if (fresh.status !== 'generating') draft.value = unwrapAiHtml(fresh.text_content);
            } catch (err) {
                // Falha de rede no polling: mantém o que está na tela e tenta de novo
            } finally {
                syncPolling();
            }
        }

        async function loadPage() {
            loading.value = true;

            const aciasUuid = route.params.acias_uuid;
            if (!aciasUuid) {
                router.push({ name: 'patients' });
                return;
            }

            try {
                const { data } = await AciasService.getByUUID(aciasUuid);
                acias.value = (data && data.data) || null;
                draft.value = unwrapAiHtml(acias.value?.text_content);
            } catch (err) {
                await Swal({
                    title: 'Ocorreu um problema ao abrir a ACIA',
                    text: 'Atualize a página e tente novamente. Se o erro persistir, entre em contato com o suporte.',
                    icon: 'error'
                });
            } finally {
                loading.value = false;
                syncPolling();
            }
        }

        async function save() {
            if (!acias.value?.uuid) return;

            try {
                saving.value = true;
                await AciasService.updateText(acias.value.uuid, draft.value);

                await Swal({
                    title: 'ACIA atualizada',
                    text: 'As alterações do fisioterapeuta foram salvas com sucesso.',
                    icon: 'success'
                });
            } catch (err) {
                await Swal({
                    title: 'Ocorreu um problema ao salvar',
                    text: 'Atualize a página e tente novamente. Se o erro persistir, entre em contato com o suporte.',
                    icon: 'error'
                });
            } finally {
                saving.value = false;
            }
        }

        async function exportPdf() {
            if (!acias.value?.uuid) return;

            try {
                exportingPdf.value = true;
                const { data } = await AciasService.exportPdf(acias.value.uuid);
                const pdfUrl = data?.data?.pdf_url;

                if (pdfUrl) {
                    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
                }
            } catch (err) {
                await Swal({
                    title: 'Ocorreu um problema ao exportar o PDF',
                    text: 'Tente novamente em instantes. Se o erro persistir, entre em contato com o suporte.',
                    icon: 'error'
                });
            } finally {
                exportingPdf.value = false;
            }
        }

        async function showUsageLimitModal() {
            let usage = null;
            try {
                const { data } = await UsageService.getUsage();
                usage = (data && data.data) || null;
            } catch (err) { /* segue com modal genérico */ }

            if (usage && usage.plan && usage.plan !== highestPlanId) {
                const shouldUpgrade = await Swal({
                    title: 'Limite de ACIA atingido',
                    text: 'Seu plano atual não possui mais créditos de ACIA neste ciclo. Faça upgrade para continuar cruzando exames com IA.',
                    icon: 'warning',
                    buttons: {
                        cancel: 'Agora não',
                        confirm: {
                            text: 'Ver planos',
                            value: true
                        }
                    }
                });

                if (shouldUpgrade) {
                    router.push({ name: 'subscription' });
                }
                return;
            }

            await Swal({
                title: 'Limite mensal de ACIA atingido',
                text: 'Você já está no melhor plano e os créditos de ACIA deste ciclo foram consumidos. Eles renovam no próximo mês.',
                icon: 'warning'
            });
        }

        async function regenerate() {
            // Sem record_uuid é ACIA só com documentos — regenera do mesmo jeito
            const recordUuid = acias.value?.record_uuid || null;
            const fileIds = (acias.value?.files_used || []).map((file) => file.id).filter(Boolean);

            if (!fileIds.length) {
                await Swal({
                    title: 'Não foi possível regenerar',
                    text: 'Esta ACIA não tem arquivos vinculados. Crie uma nova a partir do prontuário.',
                    icon: 'warning'
                });
                return;
            }

            try {
                regenerating.value = true;
                const { data } = await AciasService.generate(recordUuid, fileIds, acias.value?.patient_uuid || route.params.patient_uuid);
                const generated = (data && data.data) || null;

                if (generated?.uuid) {
                    // Cada geração é um novo registro — troca a URL para a nova ACIA.
                    // Ela nasce em 'generating': a tela passa a acompanhar por polling.
                    await router.replace({
                        name: 'aciasDetail',
                        params: { patient_uuid: route.params.patient_uuid, acias_uuid: generated.uuid }
                    });
                    await loadPage();
                }
            } catch (err) {
                const errorCode = err?.response?.data?.error;

                if (errorCode === 'usage_limit_reached') {
                    await showUsageLimitModal();
                    return;
                }

                if (errorCode === 'openrouter_not_configured') {
                    await Swal({
                        title: 'IA indisponível',
                        text: 'A integração com OpenRouter ainda não está configurada neste ambiente.',
                        icon: 'warning'
                    });
                    return;
                }

                await Swal({
                    title: 'Falha ao gerar a ACIA',
                    text: 'Não foi possível iniciar a geração agora. Tente novamente em instantes.',
                    icon: 'error'
                });
            } finally {
                regenerating.value = false;
            }
        }

        function goToRecords() {
            const patientUuid = route.params.patient_uuid || acias.value?.patient_uuid;
            if (!patientUuid) return;
            router.push({ name: 'records', params: { uuid: patientUuid } });
        }

        function goToAvaliation() {
            const patientUuid = route.params.patient_uuid || acias.value?.patient_uuid;
            if (!patientUuid || !acias.value?.record_uuid) return;

            router.push({
                name: 'avaliation',
                params: {
                    patient_uuid: patientUuid,
                    record_uuid: acias.value.record_uuid
                }
            });
        }

        function documentTypeLabel(type) {
            const labels = {
                exame_laboratorial: 'Laboratorial',
                exame_imagem: 'Imagem',
                teste_forca: 'Força',
                relatorio_medico: 'Relatório',
                outro: 'Outro'
            };
            return labels[type] || 'Documento';
        }

        function strengthLabel(strength) {
            const labels = { forte: 'Forte', moderada: 'Moderada', hipotetica: 'Hipotética' };
            return labels[strength] || 'Correlação';
        }

        function strengthClass(strength) {
            if (strength === 'forte') return 'bg-emerald-50 text-emerald-700';
            if (strength === 'moderada') return 'bg-amber-50 text-amber-700';
            return 'bg-slate-100 text-slate-500';
        }

        function formatDate(dateStr) {
            if (!dateStr) return '—';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '—';
            return date.toLocaleDateString('pt-BR');
        }

        function formatDateTime(dateStr) {
            if (!dateStr) return '—';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '—';
            return date.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }

        function formatRelative(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '';

            const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
            if (diffSec < 60) return 'agora';
            const diffMin = Math.floor(diffSec / 60);
            if (diffMin < 60) return `há ${diffMin} min`;
            const diffHour = Math.floor(diffMin / 60);
            if (diffHour < 24) return `há ${diffHour}h`;
            const diffDay = Math.floor(diffHour / 24);
            if (diffDay < 30) return `há ${diffDay} ${diffDay === 1 ? 'dia' : 'dias'}`;
            const diffMonth = Math.floor(diffDay / 30);
            if (diffMonth < 12) return `há ${diffMonth} ${diffMonth === 1 ? 'mês' : 'meses'}`;
            const diffYear = Math.floor(diffMonth / 12);
            return `há ${diffYear} ${diffYear === 1 ? 'ano' : 'anos'}`;
        }

        return {
            loading,
            saving,
            exportingPdf,
            regenerating,
            acias,
            draft,
            failureMessage,
            save,
            exportPdf,
            regenerate,
            goToRecords,
            goToAvaliation,
            documentTypeLabel,
            strengthLabel,
            strengthClass,
            formatDate,
            formatDateTime,
            formatRelative
        };
    }
};
</script>

<style scoped>
.acia-ai-cta {
    position: relative;
    overflow: hidden;
    border-color: rgba(8, 145, 178, 0.55) !important;
    background: linear-gradient(135deg, #0891b2 0%, #06b6d4 45%, #0e7490 100%) !important;
    color: #ffffff !important;
    box-shadow: 0 4px 10px rgba(8, 145, 178, 0.16);
}

.acia-ai-cta::before {
    content: '';
    position: absolute;
    top: 0;
    left: 100%;
    width: 60%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.45), transparent);
    animation: acia-ai-shine 2s linear infinite;
}

.acia-ai-cta:hover {
    border-color: rgba(14, 116, 144, 1) !important;
    box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.14), 0 6px 14px rgba(8, 145, 178, 0.22);
}

.acia-ai-cta:disabled::before,
.acia-ai-cta:disabled {
    animation: none;
}

.acia-ai-cta:disabled::before {
    display: none;
}

@keyframes acia-ai-shine {
    0% { left: 100%; }
    100% { left: -60%; }
}
</style>
